import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Database, FileText, Shield, Clock, CheckCircle2, AlertCircle, HardDrive } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface BackupRecord {
  id: string;
  type: 'database' | 'documents' | 'full';
  timestamp: Date;
  size: string;
  status: 'success' | 'error';
}

const AdminBackup = () => {
  const { toast } = useToast();
  const [backupHistory, setBackupHistory] = useState<BackupRecord[]>([]);
  const [isBackingUp, setIsBackingUp] = useState(false);

  useEffect(() => {
    // Load backup history from localStorage
    const saved = localStorage.getItem('backup-history');
    if (saved) {
      const parsed = JSON.parse(saved);
      setBackupHistory(parsed.map((b: any) => ({
        ...b,
        timestamp: new Date(b.timestamp)
      })));
    }
  }, []);

  const saveBackupHistory = (newBackup: BackupRecord) => {
    const updated = [newBackup, ...backupHistory].slice(0, 20); // Keep last 20
    setBackupHistory(updated);
    localStorage.setItem('backup-history', JSON.stringify(updated));
  };

  const exportDatenschutzDokumente = async () => {
    const documents = {
      datenschutzerklaerung: localStorage.getItem('datenschutzerklaerung') || '',
      verarbeitungsverzeichnis: localStorage.getItem('verarbeitungsverzeichnis') || '',
      auftragsverarbeitung: localStorage.getItem('auftragsverarbeitung') || '',
      betroffenenrechte: localStorage.getItem('betroffenenrechte-erledigt') || '',
      cookieConsent: localStorage.getItem('cookie-consent') || '',
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(documents, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Datenschutz_Backup_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    const backupRecord: BackupRecord = {
      id: crypto.randomUUID(),
      type: 'documents',
      timestamp: new Date(),
      size: `${(blob.size / 1024).toFixed(2)} KB`,
      status: 'success'
    };
    saveBackupHistory(backupRecord);

    toast({
      title: "Datenschutz-Dokumente exportiert",
      description: "Alle Datenschutz-Dokumente wurden gesichert.",
    });
  };

  const exportDatabaseBackup = async () => {
    setIsBackingUp(true);
    try {
      // Export appointments
      const { data: appointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select('*');

      if (appointmentsError) throw appointmentsError;

      // Export contact submissions
      const { data: contacts, error: contactsError } = await supabase
        .from('contact_submissions')
        .select('*');

      if (contactsError) throw contactsError;

      const backup = {
        timestamp: new Date().toISOString(),
        version: '1.0',
        tables: {
          appointments: appointments || [],
          contact_submissions: contacts || []
        },
        metadata: {
          exportedBy: 'admin',
          recordCount: {
            appointments: appointments?.length || 0,
            contacts: contacts?.length || 0
          }
        }
      };

      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Datenbank_Backup_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const backupRecord: BackupRecord = {
        id: crypto.randomUUID(),
        type: 'database',
        timestamp: new Date(),
        size: `${(blob.size / 1024).toFixed(2)} KB`,
        status: 'success'
      };
      saveBackupHistory(backupRecord);

      toast({
        title: "Datenbank-Backup erstellt",
        description: `${backup.metadata.recordCount.appointments + backup.metadata.recordCount.contacts} Einträge wurden exportiert.`,
      });
    } catch (error) {
      console.error('Backup error:', error);
      const backupRecord: BackupRecord = {
        id: crypto.randomUUID(),
        type: 'database',
        timestamp: new Date(),
        size: '0 KB',
        status: 'error'
      };
      saveBackupHistory(backupRecord);

      toast({
        title: "Backup fehlgeschlagen",
        description: "Fehler beim Erstellen des Datenbank-Backups.",
        variant: "destructive"
      });
    } finally {
      setIsBackingUp(false);
    }
  };

  const createFullBackup = async () => {
    await exportDatabaseBackup();
    await exportDatenschutzDokumente();

    const backupRecord: BackupRecord = {
      id: crypto.randomUUID(),
      type: 'full',
      timestamp: new Date(),
      size: 'Multiple files',
      status: 'success'
    };
    saveBackupHistory(backupRecord);

    toast({
      title: "Vollständiges Backup erstellt",
      description: "Alle Daten wurden erfolgreich gesichert.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs items={[
            { label: "Admin" },
            { label: "Backup & Wiederherstellung" }
          ]} />

          <div className="mb-8 mt-6">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
              <div>
                <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                  Backup & Wiederherstellung
                </h1>
                <p className="text-xl text-muted-foreground">
                  Datensicherung für Homepage und Datenschutz-Compliance
                </p>
              </div>
              <Button 
                onClick={createFullBackup} 
                className="flex items-center gap-2"
                disabled={isBackingUp}
              >
                <HardDrive className="w-4 h-4" />
                Vollständiges Backup
              </Button>
            </div>
          </div>

          {/* Wichtige Hinweise */}
          <Alert className="mb-8 border-primary/50">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>DSGVO-Anforderung:</strong> Regelmäßige Backups sind Teil der technischen und organisatorischen 
              Maßnahmen (TOMs) nach Art. 32 DSGVO. Führen Sie mindestens wöchentlich Backups durch und testen Sie 
              die Wiederherstellung regelmäßig.
            </AlertDescription>
          </Alert>

          {/* Supabase automatische Backups Info */}
          <Card className="p-6 mb-8 bg-muted/30 border-2 border-primary/20">
            <div className="flex items-start gap-4">
              <Database className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Lovable Cloud Automatische Backups
                </h2>
                <p className="text-muted-foreground mb-3">
                  Ihre Datenbank wird automatisch durch Lovable Cloud (Supabase) gesichert:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Tägliche Backups:</strong> Automatisch um 2:00 Uhr (UTC)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Aufbewahrung:</strong> 7 Tage (Free Plan) / 30 Tage (Paid Plans)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Point-in-Time Recovery:</strong> Verfügbar für Paid Plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Speicherort:</strong> EU-Region (Frankfurt) – DSGVO-konform</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="manual" className="shrink-0">Manuelle Backups</TabsTrigger>
              <TabsTrigger value="history" className="shrink-0">Backup-Historie</TabsTrigger>
              <TabsTrigger value="restore" className="shrink-0">Wiederherstellung</TabsTrigger>
            </TabsList>

            {/* Manuelle Backups */}
            <TabsContent value="manual">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Datenbank Backup */}
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Database className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        Datenbank-Backup
                      </h3>
                      <Badge variant="secondary" className="mt-1">
                        Appointments, Kontakte
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Exportiert alle Termine und Kontaktanfragen aus der Datenbank.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Alle Terminbuchungen
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Kontaktformular-Einträge
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Metadaten & Zeitstempel
                    </li>
                  </ul>
                  <Button 
                    onClick={exportDatabaseBackup}
                    disabled={isBackingUp}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Datenbank sichern
                  </Button>
                </Card>

                {/* Datenschutz-Dokumente Backup */}
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        Datenschutz-Dokumente
                      </h3>
                      <Badge variant="secondary" className="mt-1">
                        DSGVO-Compliance
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Sichert alle Datenschutz-relevanten Dokumente und Einstellungen.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Datenschutzerklärung
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Verarbeitungsverzeichnis
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Betroffenenrechte-Status
                    </li>
                  </ul>
                  <Button 
                    onClick={exportDatenschutzDokumente}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Dokumente sichern
                  </Button>
                </Card>

                {/* Vollständiges Backup */}
                <Card className="p-6 border-2 border-primary/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <HardDrive className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        Vollständiges Backup
                      </h3>
                      <Badge className="mt-1">
                        Empfohlen
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Kombiniert alle Backup-Typen für maximale Sicherheit.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Alle Datenbank-Einträge
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Datenschutz-Dokumente
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Konfigurationen
                    </li>
                  </ul>
                  <Button 
                    onClick={createFullBackup}
                    disabled={isBackingUp}
                    className="w-full"
                  >
                    <HardDrive className="w-4 h-4 mr-2" />
                    Vollbackup erstellen
                  </Button>
                </Card>
              </div>
            </TabsContent>

            {/* Backup-Historie */}
            <TabsContent value="history">
              <Card className="p-6">
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Letzte Backups
                </h3>
                {backupHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Noch keine Backups erstellt. Starten Sie Ihr erstes Backup oben.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {backupHistory.map((backup) => (
                      <div 
                        key={backup.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            backup.status === 'success' ? 'bg-green-500/10' : 'bg-red-500/10'
                          }`}>
                            {backup.status === 'success' ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground">
                                {backup.type === 'database' ? 'Datenbank' :
                                 backup.type === 'documents' ? 'Datenschutz-Dokumente' :
                                 'Vollständiges Backup'}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {backup.size}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {format(backup.timestamp, "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de })}
                            </p>
                          </div>
                        </div>
                        <Badge variant={backup.status === 'success' ? 'default' : 'destructive'}>
                          {backup.status === 'success' ? 'Erfolgreich' : 'Fehlgeschlagen'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Wiederherstellung */}
            <TabsContent value="restore">
              <Card className="p-6">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Backup wiederherstellen
                </h3>
                
                <Alert className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Vorsicht:</strong> Die Wiederherstellung überschreibt bestehende Daten. 
                    Erstellen Sie vorher ein aktuelles Backup!
                  </AlertDescription>
                </Alert>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">
                      Anleitung zur Wiederherstellung
                    </h4>
                    <ol className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                          1
                        </span>
                        <span className="pt-0.5">
                          <strong className="text-foreground">Backup-Datei auswählen:</strong> Wählen Sie die JSON-Backup-Datei, 
                          die Sie wiederherstellen möchten.
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                          2
                        </span>
                        <span className="pt-0.5">
                          <strong className="text-foreground">Datenbank-Backups:</strong> Verwenden Sie die Supabase Dashboard 
                          oder SQL-Importer für Datenbank-Wiederherstellungen.
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                          3
                        </span>
                        <span className="pt-0.5">
                          <strong className="text-foreground">Datenschutz-Dokumente:</strong> Öffnen Sie die JSON-Datei 
                          und übertragen Sie die Inhalte manuell in die entsprechenden Admin-Bereiche.
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                          4
                        </span>
                        <span className="pt-0.5">
                          <strong className="text-foreground">Überprüfung:</strong> Testen Sie nach der Wiederherstellung 
                          alle Funktionen und Daten auf Vollständigkeit.
                        </span>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Automatische Wiederherstellung (Lovable Cloud)
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Für die Wiederherstellung aus automatischen Backups kontaktieren Sie den Lovable Support 
                      über das Backend-Management.
                    </p>
                    <Button variant="outline" className="gap-2">
                      <Database className="w-4 h-4" />
                      Backend öffnen
                    </Button>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Best Practices */}
          <Card className="p-6 mt-8 border-2 border-primary/20">
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              Backup Best Practices für DSGVO-Compliance
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Backup-Strategie</h3>
                <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                  <li>Tägliche automatische Backups (durch Lovable Cloud)</li>
                  <li>Wöchentliche manuelle Vollbackups</li>
                  <li>Monatliche Test-Wiederherstellungen</li>
                  <li>Vor größeren Änderungen: Manuelles Backup</li>
                  <li>Aufbewahrung: Mindestens 30 Tage</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Datenschutz-Anforderungen</h3>
                <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                  <li>Backups verschlüsselt speichern</li>
                  <li>Zugriff nur für autorisierte Personen</li>
                  <li>Backup-Strategie dokumentieren (TOMs)</li>
                  <li>Regelmäßige Wiederherstellungstests</li>
                  <li>Löschkonzept für alte Backups</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminBackup;
